import type { OfferTerms } from './curatorOfferLibrary';
import { MAX_CURATOR_OFFER_GENRES } from '../constants/curatorOfferGenres';

export const validateOfferLabel = (label: string): string | undefined => {
  const trimmed = label?.trim();
  if (!trimmed) {
    return 'Offer name is required';
  }
  if (trimmed.length > 80) {
    return 'Offer name cannot exceed 80 characters';
  }
  return undefined;
};

export const validateOfferTermsField = (
  field: string,
  data: OfferTerms
): string | undefined => {
  switch (field) {
    case 'dailyStreamsMin':
      if (!data.dailyStreamsMin || data.dailyStreamsMin <= 0) {
        return 'Minimum daily streams must be a positive number';
      }
      break;
    case 'dailyStreamsMax':
      if (!data.dailyStreamsMax || data.dailyStreamsMax <= 0) {
        return 'Maximum daily streams must be a positive number';
      }
      if (data.dailyStreamsMin && data.dailyStreamsMax < data.dailyStreamsMin) {
        return 'Maximum must be greater than or equal to minimum';
      }
      break;
    case 'defaultRatePer1k':
      if (
        data.defaultRatePer1k === null ||
        data.defaultRatePer1k === undefined ||
        !Number.isFinite(Number(data.defaultRatePer1k)) ||
        Number(data.defaultRatePer1k) <= 0
      ) {
        return 'Rate per 1K must be a positive number';
      }
      break;
    case 'notes':
      if (String(data.notes || '').length > 500) {
        return 'Notes cannot exceed 500 characters';
      }
      break;
    case 'genres':
      if (!data.genres?.length) {
        return 'Select at least one genre';
      }
      if (data.genres.length > MAX_CURATOR_OFFER_GENRES) {
        return `Select at most ${MAX_CURATOR_OFFER_GENRES} genres`;
      }
      break;
    case 'interestedInFeedback':
      if (typeof data.interestedInFeedback !== 'boolean') {
        return 'Please select Yes or No';
      }
      break;
    case 'audienceCountries':
      if (data.audienceCountriesUnknown) return undefined;
      if (!data.audienceCountryCodes?.length) {
        return 'Select up to 5 countries, or choose "I don\'t know"';
      }
      if (data.audienceCountryCodes.length > 5) {
        return 'Select at most 5 countries';
      }
      break;
    default:
      break;
  }
  return undefined;
};

export const validateOfferTerms = (data: OfferTerms): Record<string, string> => {
  const errors: Record<string, string> = {};
  (
    [
      'dailyStreamsMin',
      'dailyStreamsMax',
      'defaultRatePer1k',
      'notes',
      'genres',
      'interestedInFeedback',
      'audienceCountries',
    ] as const
  ).forEach(field => {
    const error = validateOfferTermsField(field, data);
    if (error) {
      errors[field] = error;
    }
  });
  return errors;
};

export const countValidationErrors = (errors: Record<string, string | undefined>) =>
  Object.values(errors).filter(Boolean).length;

export const isOfferDraftComplete = (
  terms: OfferTerms,
  profileIds: string[],
  profilePlaylistState: Record<string, { loading?: boolean; selectedIds?: string[] }>
): boolean => {
  const termsErrors = validateOfferTerms(terms);
  const profileErrors = validateOfferProfiles(profileIds, profilePlaylistState);
  return countValidationErrors({ ...termsErrors, ...profileErrors }) === 0;
};

export const validateOfferProfiles = (
  profileIds: string[],
  profilePlaylistState: Record<string, { loading?: boolean; selectedIds?: string[] }>
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!profileIds.length) {
    errors.profiles = 'Add at least one Spotify profile to this offer';
  }

  profileIds.forEach(profileId => {
    const state = profilePlaylistState[profileId];
    if (!state || state.loading) {
      errors[profileId] = 'Playlists are still loading';
      return;
    }
    if (!state.selectedIds?.length) {
      errors[profileId] = 'Select at least one playlist';
    }
  });

  return errors;
};
